import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line } from 'recharts';
import { useNavigate } from 'react-router-dom';
import logger from '../utils/logger';

// Custom tick component for multi-line industry names
const CustomXAxisTick = ({ x, y, payload }) => {
  const text = payload.value || 'Unknown';
  const words = text.split(' ');
  
  // Split into two lines if there are multiple words
  let line1 = text;
  let line2 = '';
  
  if (words.length > 1) {
    const mid = Math.ceil(words.length / 2);
    line1 = words.slice(0, mid).join(' ');
    line2 = words.slice(mid).join(' ');
  } else if (text.length > 15) {
    // For single long words, split at a reasonable point
    line1 = text.substring(0, Math.ceil(text.length / 2));
    line2 = text.substring(Math.ceil(text.length / 2));
  }
  
  return (
    <g transform={`translate(${x},${y})`}>
      <text 
        x={0} 
        y={0} 
        dy={20} 
        textAnchor="middle" 
        fill="#374151" 
        fontSize={12}
        fontWeight={500}
        className="dark:fill-gray-300"
      >
        {line1}
      </text>
      {line2 && (
        <text 
          x={0} 
          y={0} 
          dy={36} 
          textAnchor="middle" 
          fill="#374151" 
          fontSize={12}
          fontWeight={500}
          className="dark:fill-gray-300"
        >
          {line2}
        </text>
      )}
    </g>
  );
};

const ConversionFunnelDashboard = () => {
  const navigate = useNavigate();
  const [funnelData, setFunnelData] = useState([]);
  const [weeklyData, setWeeklyData] = useState([]);
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [timeRange, setTimeRange] = useState(30);
  const [isLoading, setIsLoading] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [totalStats, setTotalStats] = useState({
    total_assessments: 0,
    total_completed: 0,
    total_consultant_led: 0,
    total_self_guided: 0
  });

  useEffect(() => {
    fetchIndustries();
    fetchFunnelData();
    fetchWeeklyData();
  }, [selectedIndustry, timeRange]);

  const fetchIndustries = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/industries`, {
        credentials: 'include'
      });
      const data = await response.json();
      
      // Deduplicate industries by name
      const uniqueIndustries = [];
      const seenNames = new Set();
      
      (data.industries || []).forEach(industry => {
        if (!seenNames.has(industry.name)) {
          seenNames.add(industry.name);
          uniqueIndustries.push(industry);
        }
      });
      
      setIndustries(uniqueIndustries);
    } catch (error) {
      logger.error('Error fetching industries:', error);
    }
  };

  const fetchFunnelData = async () => {
    setIsLoading(true);
    try {
      const industryParam = selectedIndustry ? `&industry=${encodeURIComponent(selectedIndustry)}` : '';
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/conversion/funnel/current?days_back=${timeRange}${industryParam}`;
      
      const response = await fetch(url, { credentials: 'include' });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      setFunnelData(data.funnel_data || []);
      
      // Calculate totals
      const totals = (data.funnel_data || []).reduce((acc, item) => {
        acc.total_assessments += item.assessments_started || 0;
        acc.total_completed += item.assessments_completed || 0;
        acc.total_consultant_led += item.consultant_led || 0;
        acc.total_self_guided += item.self_guided || 0;
        return acc;
      }, {
        total_assessments: 0,
        total_completed: 0,
        total_consultant_led: 0,
        total_self_guided: 0
      });
      
      setTotalStats(totals);
    } catch (error) {
      logger.error('Error fetching funnel data:', error);
      setFunnelData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchWeeklyData = async () => {
    try {
      const industryParam = selectedIndustry ? `&industry=${encodeURIComponent(selectedIndustry)}` : '';
      const weeksBack = Math.ceil(timeRange / 7);
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/conversion/funnel/weekly?weeks_back=${weeksBack}${industryParam}`,
        { credentials: 'include' }
      );
      const data = await response.json();
      
      setWeeklyData(data.weekly_data || []);
    } catch (error) {
      logger.error('Error fetching weekly data:', error);
      setWeeklyData([]);
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-4 border border-gray-300 rounded-lg shadow-lg">
          <p className="font-semibold text-gray-900 mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.value}
              {entry.name.includes('Rate') ? '%' : ''}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const completionRate = totalStats.total_assessments > 0 
    ? ((totalStats.total_completed / totalStats.total_assessments) * 100).toFixed(1)
    : 0;

  const consultantLedRate = totalStats.total_assessments > 0
    ? ((totalStats.total_consultant_led / totalStats.total_assessments) * 100).toFixed(1)
    : 0;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Assessment Analytics</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Track assessment completion and engagement metrics</p>
            </div>
            <button
              onClick={() => navigate('/admin')}
              className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Time Range
              </label>
              <select
                value={timeRange}
                onChange={(e) => setTimeRange(Number(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value={7}>Last 7 days</option>
                <option value={14}>Last 14 days</option>
                <option value={30}>Last 30 days</option>
                <option value={60}>Last 60 days</option>
                <option value={90}>Last 90 days</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Industry Filter
              </label>
              <select
                value={selectedIndustry}
                onChange={(e) => setSelectedIndustry(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-emerald-500 dark:focus:ring-emerald-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">All Industries</option>
                {industries.map(industry => {
                  const industryName = typeof industry === 'string' ? industry : industry.name;
                  const region = typeof industry === 'string' ? '' : (industry.region || '');
                  const displayName = region ? `${industryName} (${region.toUpperCase()})` : industryName;
                  return (
                    <option key={typeof industry === 'string' ? industry : industry.id} value={industryName}>
                      {displayName}
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Assessments</p>
                <p className="text-3xl font-bold text-gray-900 dark:text-white mt-2">{totalStats.total_assessments}</p>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl">
                <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Completed</p>
                <p className="text-3xl font-bold text-emerald-600 dark:text-emerald-400 mt-2">{totalStats.total_completed}</p>
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">{completionRate}% rate</p>
              </div>
              <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl">
                <svg className="w-6 h-6 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Consultant-Led</p>
                <p className="text-3xl font-bold text-purple-600 dark:text-purple-400 mt-2">{totalStats.total_consultant_led}</p>
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">{consultantLedRate}% of total</p>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                <svg className="w-6 h-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700 hover:shadow-xl transition-shadow duration-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Self-Guided</p>
                <p className="text-3xl font-bold text-orange-600 dark:text-orange-400 mt-2">{totalStats.total_self_guided}</p>
                <p className="text-sm text-gray-500 dark:text-gray-500 mt-1">{(100 - consultantLedRate).toFixed(1)}% of total</p>
              </div>
              <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-xl">
                <svg className="w-6 h-6 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Industry Breakdown Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8 mb-8">
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Assessments by Industry</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Distribution of assessments across different industries</p>
          </div>
          {isLoading ? (
            <div className="flex items-center justify-center h-96">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-500"></div>
            </div>
          ) : funnelData.length === 0 ? (
            <div className="flex items-center justify-center h-96 text-gray-500 dark:text-gray-400">
              <div className="text-center">
                <svg className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <p>No assessment data available for the selected period</p>
              </div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={550}>
              <BarChart 
                data={funnelData}
                margin={{ top: 30, right: 40, left: 30, bottom: 140 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" className="dark:stroke-gray-700" />
                <XAxis 
                  dataKey="industry" 
                  stroke="#9CA3AF" 
                  angle={0}
                  textAnchor="middle" 
                  height={140}
                  interval={0}
                  tick={<CustomXAxisTick />}
                  axisLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                  tickLine={{ stroke: '#D1D5DB', strokeWidth: 1, length: 5 }}
                />
                <YAxis 
                  stroke="#9CA3AF"
                  tick={{ fontSize: 12, fill: '#6B7280', fontWeight: 500 }}
                  axisLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                  tickLine={{ stroke: '#D1D5DB', strokeWidth: 1 }}
                  label={{ value: 'Number of Assessments', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#6B7280', fontSize: 12, fontWeight: 500 } }}
                />
                <Tooltip 
                  content={<CustomTooltip />}
                  cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
                />
                <Legend 
                  wrapperStyle={{ paddingTop: '30px', fontSize: '13px', fontWeight: 500 }}
                  iconType="rect"
                  iconSize={14}
                />
                <Bar dataKey="assessments_started" fill="#3B82F6" name="Started" radius={[6, 6, 0, 0]} />
                <Bar dataKey="assessments_completed" fill="#10B981" name="Completed" radius={[6, 6, 0, 0]} />
                <Bar dataKey="consultant_led" fill="#8B5CF6" name="Consultant-Led" radius={[6, 6, 0, 0]} />
                <Bar dataKey="self_guided" fill="#F59E0B" name="Self-Guided" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Weekly Trend Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-8 mb-8">
          <div className="mb-8">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Weekly Trend</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">Assessment activity over time</p>
          </div>
          {weeklyData.length === 0 ? (
            <div className="flex items-center justify-center h-96 text-gray-500 dark:text-gray-400">
              <div className="text-center">
                <svg className="w-12 h-12 mx-auto mb-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                </svg>
                <p>No weekly trend data available</p>
              </div>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={weeklyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="week" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line type="monotone" dataKey="assessments_started" stroke="#3B82F6" strokeWidth={2} name="Started" />
                <Line type="monotone" dataKey="assessments_completed" stroke="#10B981" strokeWidth={2} name="Completed" />
                <Line type="monotone" dataKey="consultant_led" stroke="#8B5CF6" strokeWidth={2} name="Consultant-Led" />
                <Line type="monotone" dataKey="self_guided" stroke="#F59E0B" strokeWidth={2} name="Self-Guided" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Detailed Table */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Industry Breakdown</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Detailed assessment metrics by industry</p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Industry</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Started</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Completed</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Consultant-Led</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Self-Guided</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Completion Rate</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {funnelData.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                      No data available for the selected period
                    </td>
                  </tr>
                ) : (
                  funnelData.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">{item.industry}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{item.assessments_started}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{item.assessments_completed}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{item.consultant_led}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">{item.self_guided}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item.completion_rate >= 80 ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' :
                          item.completion_rate >= 50 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' :
                          'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                          {item.completion_rate}%
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConversionFunnelDashboard;
